import React, { FC, useContext } from 'react'
import styled from 'styled-components'

import {
  Flex,
  Button,
  AnchorButton,
  DownloadFileAnchor,
  ErrorMessage,
} from '@sketch/components'
import Footer from 'components/Footer'
import ModalContext from 'containers/ModalManager/ModalContext'
import { ShareInfoFragment } from '@sketch/gql-types'
import { ReactComponent as TrashLarge } from '@sketch/icons/trash-bin-64-svg'
import { RestoreShareModal } from 'modules/shares/components/RestoreShareModal'
import { getDownloadProps } from 'modules/shares/utils'
import Navbar from 'components/Navbar'
import { IsSignedIn } from 'containers/IsSignedIn'
import HeaderWorkspaceBadge from '../components/HeaderWorkspaceBadge'
import { SidebarLayout } from 'layouts/SidebarLayout'
import ShareWorkspaceSidebar from '../components/ShareWorkspaceSidebar'

export type ShareForDeletedView = Pick<
  ShareInfoFragment,
  | 'name'
  | 'identifier'
  | 'version'
  | 'workspace'
  | 'userCanEdit'
  | 'userCanUpdateSettings'
>

export interface ShareDeletedViewProps {
  share: ShareForDeletedView
}

const DownloadButton = styled(DownloadFileAnchor).attrs({
  as: AnchorButton,
  variant: 'secondary',
  size: '40',
})``

export const ShareDeletedView: FC<ShareDeletedViewProps> = ({ share }) => {
  const { name, identifier } = share
  const { showModal } = useContext(ModalContext)

  const title = 'Document is in the Trash'
  const download = getDownloadProps(share.version)
  const userCanRestoreDocument =
    share.userCanEdit || share.userCanUpdateSettings

  const onRestore = () => {
    showModal(RestoreShareModal, {
      name,
      shareIdentifier: identifier,
    })
  }

  return (
    <SidebarLayout
      title={title}
      header={({ setSidebarLeftOpen }) => (
        <Navbar>
          <IsSignedIn>
            <HeaderWorkspaceBadge
              workspace={share.workspace!}
              setSidebarLeftOpen={setSidebarLeftOpen}
            />
          </IsSignedIn>
        </Navbar>
      )}
      sidebarLeft={
        <ShareWorkspaceSidebar
          workspaceIdentifier={share.workspace.identifier}
        />
      }
      footer={<Footer />}
    >
      <ErrorMessage
        icon={<TrashLarge />}
        iconSize="medium"
        title={title}
        extra={
          <Flex>
            {download?.isAvailable && (
              <DownloadButton href={download.url}>Download</DownloadButton>
            )}
            {userCanRestoreDocument && (
              <Button variant="secondary" onClick={onRestore} small>
                Restore
              </Button>
            )}
          </Flex>
        }
      >
        You cannot open <b>“{share.name}”</b> because it&apos;s in the Workspace
        Trash.
      </ErrorMessage>
    </SidebarLayout>
  )
}
